import { useRef } from 'react'

import { VideoControlsAction, VideoFeedEvent } from '@/features/video-player'
import { getNetworkInfo } from '@/utils/network'

import { mixpanel } from '../mixpanel'
import {
  AnalyticsScreen,
  MixpanelEvents,
  PlayerEvents,
} from '../types/playerEvents'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventPayload = any

export const useMixpanel = (screenName?: AnalyticsScreen) => {
  const streamStartTimeRef = useRef<number | null>(null)
  const streamReceivedTimeRef = useRef<number | null>(null)
  // Timestamp of when the incident clip was requested, used to measure how long
  // it took to receive the clip URL.
  const clipRequestedTimeRef = useRef<number | null>(null)
  // Timestamp of when the incident clip URL was received, used to measure how
  // long it then took to start playing. Kept separate from the live-stream refs
  // so toggling between clip and live does not cross-contaminate the timing.
  const clipReceivedTimeRef = useRef<number | null>(null)

  const markStreamRequested = () => {
    streamStartTimeRef.current = performance.now()
    streamReceivedTimeRef.current = null
  }

  const createFullEventName = (eventName: string): string => {
    const name = screenName ? `${screenName}:` : ''
    return `${name}${eventName}`
  }

  const trackEvent = (eventName: MixpanelEvents, data?: EventPayload) => {
    const name = createFullEventName(eventName)
    mixpanel.track(name, data)
  }

  const createIncidentVideoEventHandler = (data?: EventPayload) => {
    return (event: VideoFeedEvent) => {
      switch (event) {
        case VideoFeedEvent.BeginRequesting:
          clipRequestedTimeRef.current = performance.now()
          clipReceivedTimeRef.current = null
          trackEvent(PlayerEvents.requestIncidentClip, {
            ...data,
            ...getNetworkInfo(),
          })
          return
        case VideoFeedEvent.Received: {
          const now = performance.now()
          const requestedTime = clipRequestedTimeRef.current
          clipReceivedTimeRef.current = now
          trackEvent(PlayerEvents.receivedIncidentClip, {
            ...data,
            ...getNetworkInfo(),
            request_to_received_ms:
              requestedTime != null ? Math.round(now - requestedTime) : null,
          })
          clipRequestedTimeRef.current = null
          return
        }
        case VideoFeedEvent.Playing: {
          const receivedTime = clipReceivedTimeRef.current
          trackEvent(PlayerEvents.playIncidentClip, {
            ...data,
            ...getNetworkInfo(),
            received_to_playing_ms:
              receivedTime != null
                ? Math.round(performance.now() - receivedTime)
                : null,
          })
          clipReceivedTimeRef.current = null
          return
        }
        default:
          return
      }
    }
  }

  // `properties` is only supplied by the failure leg, which carries a
  // `failure_reason` the other events have no equivalent for.
  const createCameraEventHandler = (data?: EventPayload) => {
    return (event: VideoFeedEvent, properties?: EventPayload) => {
      switch (event) {
        case VideoFeedEvent.BeginRequesting:
          streamStartTimeRef.current = performance.now()
          streamReceivedTimeRef.current = null
          trackEvent(PlayerEvents.requestLiveFeed, data)
          return
        case VideoFeedEvent.Received:
          streamReceivedTimeRef.current = performance.now()
          trackEvent(PlayerEvents.receivedLiveFeed, data)
          return
        case VideoFeedEvent.Playing: {
          trackEvent(PlayerEvents.playLiveFeed, data)
          const startTime = streamStartTimeRef.current
          const receivedTime = streamReceivedTimeRef.current
          if (startTime != null) {
            const now = performance.now()
            mixpanel.track(PlayerEvents.liveStreamLatency, {
              ...data,
              screen_name: screenName,
              total_ms: Math.round(now - startTime),
              request_ms: receivedTime
                ? Math.round(receivedTime - startTime)
                : null,
              buffer_ms: receivedTime ? Math.round(now - receivedTime) : null,
            })
            streamStartTimeRef.current = null
            streamReceivedTimeRef.current = null
          }
          return
        }
        case VideoFeedEvent.Failed:
          trackEvent(PlayerEvents.videoPlaybackError, {
            ...data,
            ...properties,
          })
          // The feed never played, so the pending timing would otherwise be
          // charged to whatever plays next.
          streamStartTimeRef.current = null
          streamReceivedTimeRef.current = null
          return
        default:
          return
      }
    }
  }

  const createPlayerActionHandler = (data?: EventPayload) => {
    return (action: VideoControlsAction) => {
      switch (action) {
        case VideoControlsAction.Backward10:
          trackEvent(PlayerEvents.backward10, data)
          return
        case VideoControlsAction.Forward10:
          trackEvent(PlayerEvents.forward10, data)
          return
        case VideoControlsAction.Play:
          trackEvent(PlayerEvents.play, data)
          return
        case VideoControlsAction.Pause:
          trackEvent(PlayerEvents.pause, data)
          return
        case VideoControlsAction.Fullscreen:
          trackEvent(PlayerEvents.fullscreen, data)
          return
        default:
          return
      }
    }
  }

  return {
    trackEvent,
    markStreamRequested,
    createPlayerActionHandler,
    createCameraEventHandler,
    createIncidentVideoEventHandler,
  }
}
